import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
// import { CalendarDays, CircleDollarSign, ReceiptText } from "lucide-react";
import { toast } from "react-toastify";

import PosSettlementTable from "../components/pos-settlement/PosSettlementTable";
import SettlePosDialog from "../components/pos-settlement/SettlePosDialog";
import {
  useGetMonthlyPosSettlementSessions,
  usePostPosSettlement,
} from "../service/counterService";
import {
  PosSettlementSession,
  PosSettlementTransactionPayload,
} from "../types/posSettlementTypes";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import { formatAppDate } from "../utils/dateFormatter";

const createInitialSettlementForm = (
  row?: PosSettlementSession | null,
): PosSettlementTransactionPayload => ({
  sessionId: row?.sessionId ?? 0,
  amount: row?.actualPos ?? 0,
});

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value || 0);

const formatDisplayDate = (value: string) => {
  return formatAppDate(value);
};

const formatApiLocalDate = (value: string) => {
  if (!value) return undefined;
  // Already in YYYY-MM-DD format from the date input
  return value;
};

function PosSettlement() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });
  const [selectedSession, setSelectedSession] =
    useState<PosSettlementSession | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formState, setFormState] = useState<PosSettlementTransactionPayload>(
    createInitialSettlementForm(),
  );

  const branchId = Number(localStorage.getItem("branchId")) || 0;

  useEffect(() => {
    setCurrentPage(0);
  }, [dateRange.start, dateRange.end]);

  const { data, isLoading } = useGetMonthlyPosSettlementSessions({
    params: {
      branchId,
      page: currentPage,
      size: pageSize,
      fromDate: dateRange.start || new Date().toISOString().slice(0, 10),
      toDate: dateRange.end || new Date().toISOString().slice(0, 10),
    },
  });

  const { mutate: settlePos, isPending: isSubmitting } = usePostPosSettlement();

  const rows = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // const summaryCards = useMemo(() => {
  //   const settledCount = rows.filter((row) => row.isPosSettled).length;
  //   const pendingCount = rows.length - settledCount;
  //   const expectedTotal = rows.reduce((sum, row) => sum + row.expectedPos, 0);

  //   return [
  //     {
  //       title: "Sessions",
  //       value: totalElements,
  //       description: "POS sessions visible for the current filters",
  //       icon: ReceiptText,
  //       iconClassName: "bg-[#E8F0FF] text-[#1A5EF9]",
  //       accentClassName: "from-[#1A5EF9]/12 to-[#1A5EF9]/0",
  //     },
  //     {
  //       title: "Pending",
  //       value: pendingCount,
  //       description: "Sessions still waiting for settlement",
  //       icon: CalendarDays,
  //       iconClassName: "bg-[#FFF5E8] text-[#F08A24]",
  //       accentClassName: "from-[#F08A24]/14 to-[#F08A24]/0",
  //     },
  //     {
  //       title: "Expected Total",
  //       value: formatCurrency(expectedTotal),
  //       description: "Expected POS amount on this page",
  //       icon: CircleDollarSign,
  //       iconClassName: "bg-[#EAFBF3] text-[#0E9F6E]",
  //       accentClassName: "from-[#0E9F6E]/12 to-[#0E9F6E]/0",
  //     },
  //   ];
  // }, [rows, totalElements]);

  const handleOpenSettlement = (row: PosSettlementSession) => {
    setSelectedSession(row);
    setFormState(createInitialSettlementForm(row));
    setIsDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedSession(null);
      setFormState(createInitialSettlementForm());
    }
  };

  const handleFieldChange = (
    field: keyof PosSettlementTransactionPayload,
    value: string | number,
  ) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!selectedSession) {
      toast.error("Please choose a session to settle.");
      return;
    }

    if (formState.amount <= 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (formState.amount > selectedSession.expectedPos) {
      toast.error(`Amount cannot be greater than expected amount.`);
      return;
    }

    settlePos(formState, {
      onSuccess: () => {
        toast.success("POS settled successfully.");
        queryClient.invalidateQueries({
          queryKey: ["useGetMonthlyPosSettlementSessions"],
        });
        handleDialogChange(false);
      },
      onError: (error: Error) => {
        toast.error(`Unable to settle POS: ${error.message}`);
      },
    });
  };

  return (
    <>
      <div className="space-y-6">
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#21314A]">
                POS Settlement
              </h1>
              <p className="mt-1 text-sm text-[#7D8998]">
                Review day-end POS sessions and settle the ones that are still
                pending.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(event) =>
                    setDateRange((prev) => ({ ...prev, start: event.target.value }))
                  }
                  className="h-11 min-w-[160px] rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  End Date
                </label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(event) =>
                    setDateRange((prev) => ({ ...prev, end: event.target.value }))
                  }
                  className="h-11 min-w-[160px] rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setDateRange({
                    start: new Date().toISOString().slice(0, 10),
                    end: new Date().toISOString().slice(0, 10),
                  })
                }
                className="mt-0 h-11 rounded-xl border-[#D8E1EC] px-5 text-slate-600 sm:mt-7"
              >
                Reset Filter
              </Button>
            </div>
          </div>
        </div>

        {/* <div className="grid gap-4 lg:grid-cols-3">
          {summaryCards.map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.title}
                className={`rounded-[24px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_34px_rgba(15,23,42,0.06)]`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-[#7D8998]">
                      {card.title}
                    </p>
                    <p className="mt-3 text-3xl font-semibold text-[#21314A]">
                      {card.value}
                    </p>
                    <p className="mt-2 text-sm text-[#7D8998]">
                      {card.description}
                    </p>
                  </div>
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.iconClassName}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
                <div
                  className={`mt-5 h-2 rounded-full bg-gradient-to-r ${card.accentClassName}`}
                />
              </div>
            );
          })}
        </div> */}

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="border-b border-[#E8EEF9] px-6 py-5">
            <h3 className="text-lg font-semibold text-[#21314A]">
              Settlement Queue
            </h3>
            <p className="mt-1 text-sm text-[#7D8998]">
              Branch #{branchId || "-"} POS sessions from the live counter feed.
            </p>
          </div>

          <PosSettlementTable
            rows={rows}
            isLoading={isLoading}
            currentPage={data?.pageNumber ?? currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            onPageChange={setCurrentPage}
            onSettle={handleOpenSettlement}
            formatCurrency={formatCurrency}
            formatDate={formatDisplayDate}
          />
        </div>
      </div>

      <SettlePosDialog
        open={isDialogOpen}
        formState={formState}
        isSubmitting={isSubmitting}
        expectedAmount={selectedSession?.expectedPos}
        onOpenChange={handleDialogChange}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default PosSettlement;
